import { useMemo, useState } from "react";
import type { ReactElement } from "react";

import { getRoiConfig, formatRubExport } from "../data/roi-configs";

/**
 * Cycle 9 STORY-801: Interactive ROI Calculator.
 *
 * Embedded on ProductDetailPage after ROI metrics section.
 * Sliders + live recalculation + savings display + payback months.
 *
 * Props: slug — product slug; lookup config from data/roi-configs.ts.
 * If config missing — component renders nothing (graceful absence).
 */

interface Props {
  slug: string;
  /** Optional accent color for sliders/buttons */
  accentColor?: string;
}

export function RoiCalculator({ slug, accentColor = "#0b5fff" }: Props): ReactElement | null {
  const config = getRoiConfig(slug);

  // Initial values from config defaults
  const [values, setValues] = useState<Record<string, number>>(() => {
    if (!config) return {};
    return Object.fromEntries(config.inputs.map((i) => [i.key, i.defaultValue]));
  });

  const result = useMemo(() => {
    if (!config) return null;
    return config.calculate(values);
  }, [config, values]);

  if (!config || !result) return null;

  return (
    <section className="roi-calculator" aria-labelledby="roi-calc-title">
      <header className="roi-calculator__header">
        <h2 id="roi-calc-title" className="roi-calculator__title">
          Калькулятор экономии под ваши цифры
        </h2>
        <p className="roi-calculator__subtitle">
          Подвиньте ползунки — расчёт обновится мгновенно. Числа — типовые отраслевые;
          реальная экономия может отличаться.
        </p>
      </header>

      <div className="roi-calculator__grid">
        <div className="roi-calculator__inputs">
          {config.inputs.map((input) => (
            <div key={input.key} className="roi-input">
              <label className="roi-input__label" htmlFor={`roi-${input.key}`}>
                {input.label}
                {input.unit && <span className="roi-input__unit"> ({input.unit})</span>}
              </label>
              <div className="roi-input__row">
                <input
                  id={`roi-${input.key}`}
                  type="range"
                  className="roi-input__slider"
                  min={input.min}
                  max={input.max}
                  step={input.step}
                  value={values[input.key] ?? input.defaultValue}
                  onChange={(e) =>
                    setValues((v) => ({
                      ...v,
                      [input.key]: Number(e.target.value),
                    }))
                  }
                  style={{ accentColor }}
                />
                <span className="roi-input__value" style={{ color: accentColor }}>
                  {values[input.key]?.toLocaleString("ru-RU") ?? input.defaultValue.toLocaleString("ru-RU")}
                </span>
              </div>
              {input.hint && <p className="roi-input__hint">{input.hint}</p>}
            </div>
          ))}
        </div>

        <div className="roi-calculator__output">
          <div className="roi-output">
            <div className="roi-output__primary">
              <div className="roi-output__label">Экономия в год</div>
              <div
                className="roi-output__value"
                style={{ color: accentColor }}
              >
                {formatRubExport(result.annualSavingsRub)}
              </div>
            </div>
            <div className="roi-output__secondary">
              <div className="roi-output__metric">
                <span className="roi-output__metric-label">Окупаемость</span>
                <span className="roi-output__metric-value">
                  {result.paybackMonths < 1
                    ? "<1 месяца"
                    : result.paybackMonths >= 60
                      ? "60+ месяцев"
                      : `${result.paybackMonths.toFixed(1)} мес`}
                </span>
              </div>
            </div>
            <details className="roi-output__breakdown">
              <summary>Как это посчитано</summary>
              <p className="roi-output__formula">
                <code>{result.formula}</code>
              </p>
              <ul className="roi-output__breakdown-list">
                {result.breakdown.map((b) => (
                  <li key={b.label}>
                    <span>{b.label}:</span>
                    <strong>{b.value}</strong>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      </div>

      <p className="roi-calculator__disclaimer">{config.disclaimer}</p>
    </section>
  );
}
